import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { isSystemAdmin } from './aivisRoles';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../services/api';
import { isSnapshotBuilding, snapshotRetryDelayMs } from '../../services/snapshotState';
import { exportCsv } from '../../utils/csvExport';
import { chartSeries, fitColors, sourceTypeColors, tokens } from './marketingTokens';
import NoClientState from './NoClientState';
import './ai-vis.css';

const PAGE_SIZE = 20;

const LINE_COLORS = chartSeries;

const TYPE_COLOR = sourceTypeColors;

const COL_TOOLTIPS = {
  url: 'The specific URL cited in AI responses.',
  classifier: 'Domain-level classification — Corporate, UGC (Reddit/forums), Editorial, Ecommerce, and more.',
  url_type: 'Page-level classification based on URL and title — Competitor, Homepage, Product Roundup, Review, Ecommerce, Video, Reddit, and more.',
  client_mentioned: "Whether the client was mentioned on this URL's page, verified by crawling the page content.",
  times_cited: 'Total number of unique AI chat responses that cited this URL across all runs for this campaign.',
  relevance_check: 'Gemini PR fit score — Good Fit means this article is a strong pitching opportunity.',
  n8n_sent: 'Whether this URL has been sent to the n8n prospecting workflow for outreach.',
};

const FIT_COLORS = fitColors;

function typeColor(classifier) {
  return TYPE_COLOR[classifier] || tokens.graphite400;
}

function typeClass(classifier) {
  const map = {
    competitor: 'aivis-dash-type--competitor',
    reddit: 'aivis-dash-type--ugc',
    product_roundup: 'aivis-dash-type--editorial',
    review: 'aivis-dash-type--review',
    ecommerce: 'aivis-dash-type--commerce',
    review_site: 'aivis-dash-type--reviewsite',
    client: 'aivis-dash-type--you',
    homepage: 'aivis-dash-type--corp',
    video: 'aivis-dash-type--video',
    other: 'aivis-dash-type--other',
  };
  return `aivis-dash-type ${map[classifier] || 'aivis-dash-type--other'}`;
}

function formatShortDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return iso; }
}

function urlDisplayFull(url, maxLen = 65) {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    const full = u.hostname + u.pathname + (u.search || '');
    return full.length > maxLen ? `${full.slice(0, maxLen)}…` : full;
  } catch {
    return url.length > maxLen ? `${url.slice(0, maxLen)}…` : url;
  }
}

function urlShortForLegend(url, maxLen = 42) {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    const full = u.hostname + u.pathname;
    return full.length > maxLen ? `${full.slice(0, maxLen)}…` : full;
  } catch {
    return url.length > maxLen ? `${url.slice(0, maxLen)}…` : url;
  }
}

/* ─── Tooltip portal ─── */
const TOOLTIP_W = 230;
const TOOLTIP_MARGIN = 12;

function TooltipPortal({ text, anchorRef, visible }) {
  const [style, setStyle] = useState({});
  useEffect(() => {
    if (!visible || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const anchorCX = r.left + r.width / 2;
    let left = anchorCX - TOOLTIP_W / 2;
    left = Math.max(TOOLTIP_MARGIN, Math.min(left, window.innerWidth - TOOLTIP_W - TOOLTIP_MARGIN));
    const arrowLeft = Math.round(Math.max(14, Math.min(anchorCX - left, TOOLTIP_W - 14)));
    setStyle({ left, top: r.top - 10, transform: 'translateY(-100%)', '--arrow-left': `${arrowLeft}px` });
  }, [visible, anchorRef]);
  if (!visible || !text) return null;
  return createPortal(
    <div className="aivis-domains-tooltip-popup" style={style} role="tooltip">{text}</div>,
    document.body
  );
}

function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  return (
    <span ref={ref} className="aivis-domains-th-info" aria-label={text} role="img"
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)} onBlur={() => setVisible(false)} tabIndex={0}
    >
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 7v5M8 5.25V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <TooltipPortal text={text} anchorRef={ref} visible={visible} />
    </span>
  );
}

function SortIcon({ active, dir }) {
  if (active) {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden className="aivis-domains-sort-icon aivis-domains-sort-icon--active">
        {dir === 'asc' ? <path d="M6 2L10.5 9H1.5L6 2Z" fill="currentColor" /> : <path d="M6 10L1.5 3H10.5L6 10Z" fill="currentColor" />}
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden className="aivis-domains-sort-icon">
      <path d="M3.5 5L6 2L8.5 5H3.5Z" fill="currentColor" opacity="0.4" />
      <path d="M3.5 7L6 10L8.5 7H3.5Z" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

function ColHeader({ label, sortKey, currentSort, currentDir, onSort, tooltip, numeric, centered }) {
  const active = currentSort === sortKey;
  const alignClass = centered ? 'aivis-domains-th-center' : (numeric ? 'aivis-domains-th-num' : '');
  return (
    <th scope="col"
      className={`${alignClass} ${sortKey ? 'aivis-domains-th-sortable' : ''}`}
      onClick={sortKey ? () => onSort(sortKey) : undefined}
      aria-sort={active ? (currentDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <div className="aivis-domains-th-inner">
        <span className="aivis-domains-th-label">{label}</span>
        {sortKey && <SortIcon active={active} dir={currentDir} />}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
    </th>
  );
}

function UrlTypeDistributionPanel({ items, totalSources }) {
  const [hoveredClassifier, setHoveredClassifier] = useState(null);
  const maxPct = Math.max(...items.map((i) => i.pct), 1);
  return (
    <div className="aivis-domains-type-panel">
      <div className="aivis-domains-type-panel-head">
        <span className="aivis-dash-card-title">URL types</span>
        {totalSources > 0 && (
          <span className="aivis-domains-type-total">
            Total: {totalSources >= 1000 ? `${(totalSources / 1000).toFixed(1)}k` : totalSources}
          </span>
        )}
      </div>
      <p className="aivis-dash-card-desc" style={{ marginBottom: 16 }}>
        Distribution of URL types across all cited sources.
      </p>
      {items.length === 0
        ? <p className="aivis-dash-empty">No source data yet.</p>
        : (
          <ul className="aivis-domains-type-list">
            {items.map((item) => {
              const isHovered = hoveredClassifier === item.classifier;
              return (
                <li
                  key={item.classifier}
                  className="aivis-domains-type-row aivis-url-type-row"
                  onMouseEnter={() => setHoveredClassifier(item.classifier)}
                  onMouseLeave={() => setHoveredClassifier(null)}
                >
                  <span className="aivis-domains-type-dot" style={{ background: typeColor(item.classifier) }} aria-hidden />
                  <span className="aivis-domains-type-name">{item.url_type_label || item.type_label}</span>
                  <div className="aivis-domains-type-bar-wrap" aria-hidden>
                    <div
                      className="aivis-domains-type-bar"
                      style={{ width: `${(item.pct / maxPct) * 100}%`, background: typeColor(item.classifier) }}
                    />
                  </div>
                  <span className="aivis-domains-type-pct">{item.pct}%</span>
                  {isHovered && (
                    <div className="aivis-url-type-tooltip" role="tooltip">
                      <strong>{item.url_type_label || item.type_label}</strong>
                      <span>{item.count.toLocaleString()} URL{item.count === 1 ? '' : 's'}</span>
                      <span>{item.pct}% of all citations</span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
}

function PaginationControls({ page, totalPages, totalItems, onPage }) {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }
  return (
    <div className="aivis-domains-pagination">
      <div className="aivis-domains-pg-controls">
        <button type="button" className="aivis-domains-pg-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous">‹</button>
        {pages.map((p, i) =>
          p === '…'
            ? <span key={`e${i}`} className="aivis-domains-pg-ellipsis">…</span>
            : <button key={p} type="button" className={`aivis-domains-pg-btn ${p === page ? 'aivis-domains-pg-btn--active' : ''}`} onClick={() => onPage(p)}>{p}</button>
        )}
        <button type="button" className="aivis-domains-pg-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Next">›</button>
      </div>
      <span className="aivis-domains-pg-count">{totalItems.toLocaleString()} item{totalItems === 1 ? '' : 's'}</span>
    </div>
  );
}

function SearchIcon() {
  return (
    <span className="aivis-prompts-search-icon" aria-hidden>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

export default function SourcesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCampaignId, campaigns, user } = useOutletContext();
  const selected = campaigns.find((c) => String(c.id) === String(selectedCampaignId));
  const initialSearch = location.state?.initialSearch || '';

  // "Sent to n8n" column: system admins only — hidden from all client accounts.
  const showN8nColumn = isSystemAdmin(user);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Seed search from navigation state (e.g. deep-link from dashboard Top Domains)
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [typeFilter, setTypeFilter] = useState('');
  const [fitFilter, setFitFilter] = useState('');
  const [n8nFilter, setN8nFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('times_cited');
  const [sortDir, setSortDir] = useState('desc');
  const [snapshotRetry, setSnapshotRetry] = useState(0);
  const searchTimeoutRef = useRef(null);

  // Track if search was seeded from navigation state so campaign-change reset doesn't wipe it
  const navSearchConsumedRef = useRef(!!location.state?.initialSearch);

  // On mount: if the state arrives after the initial render (e.g. after AiVisLayout replaces the URL),
  // apply it to the search box. Only runs once.
  useEffect(() => {
    const initial = location.state?.initialSearch;
    if (initial) {
      setSearch(initial);
      navSearchConsumedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (navSearchConsumedRef.current) {
      // First mount with a pre-seeded search — skip clearing search, reset everything else
      navSearchConsumedRef.current = false;
      setDebouncedSearch(initialSearch); setTypeFilter(''); setFitFilter(''); setN8nFilter(''); setPage(1); setData(null); setError('');
      return;
    }
    setSearch(''); setDebouncedSearch(''); setTypeFilter(''); setFitFilter(''); setN8nFilter(''); setPage(1); setData(null); setError('');
  }, [selectedCampaignId, initialSearch]);

  useEffect(() => {
    if (!selectedCampaignId) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true); setError('');
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(PAGE_SIZE),
      sort: sortKey,
      direction: sortDir,
      scored: '1',
    });
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    if (typeFilter) params.set('classifier', typeFilter);
    if (fitFilter) params.set('fit', fitFilter === 'good' ? 'Good Fit' : fitFilter === 'moderate' ? 'Moderate Fit' : 'Poor Fit');
    if (n8nFilter) params.set('n8n', n8nFilter);
    api.get(`/aivis/campaigns/${selectedCampaignId}/sources/?${params}`, { signal: controller.signal })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || 'Could not load sources.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [selectedCampaignId, page, sortKey, sortDir, debouncedSearch, typeFilter, fitFilter, n8nFilter, snapshotRetry]);

  const snapshotBuilding = isSnapshotBuilding(data);
  const snapshotRetryAfter = data?.retry_after;

  useEffect(() => {
    if (!snapshotBuilding) return undefined;
    const timer = window.setTimeout(
      () => setSnapshotRetry((attempt) => attempt + 1),
      snapshotRetryDelayMs({ retry_after: snapshotRetryAfter }),
    );
    return () => window.clearTimeout(timer);
  }, [snapshotBuilding, snapshotRetryAfter]);

  // Unique URL types derived from loaded data, sorted by count desc
  const urlTypeOptions = useMemo(() => {
    const dist = data?.type_distribution || [];
    return dist
      .slice()
      .sort((a, b) => b.count - a.count)
      .map((t) => ({ classifier: t.classifier, label: t.url_type_label || t.type_label }));
  }, [data?.type_distribution]);

  function handleSearchChange(e) {
    const val = e.target.value;
    setSearch(val); setPage(1);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 320);
  }

  function handleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
    setPage(1);
  }

  function handleExportCsv() {
    const clientSlug = selected?.client_name?.toLowerCase().replace(/\s+/g, '-') || 'client';
    const date = new Date().toISOString().slice(0, 10);
    const columns = [
      { header: '#', key: '_rank', format: (_, row) => String(sortedUrls.indexOf(row) + 1) },
      { header: 'Title', key: 'page_title', format: (v) => v || '' },
      { header: 'URL', key: 'url' },
      { header: 'URL Type', key: 'url_type_label', format: (v) => v || '' },
      { header: 'Client Mentioned', key: 'client_mentioned', format: (v) => (v ? 'Yes' : 'No') },
      { header: 'Times Cited', key: 'times_cited' },
      { header: 'Fit Score', key: 'relevance_check', format: (v) => v || '—' },
      { header: 'Sent to n8n', key: 'n8n_sent', format: (v) => (v ? 'Yes' : 'No') },
    ];
    const suffix = typeFilter ? `-${typeFilter}` : '';
    exportCsv(columns, sortedUrls, `urls-${clientSlug}${suffix}-${date}.csv`);
  }

  const sortedUrls = useMemo(() => {
    return data?.urls || [];
  }, [data?.urls]);

  const totalUrls = data?.urls_count || 0;
  const totalPages = data?.total_pages || 1;
  const safePage = Math.min(page, totalPages);
  const pageUrls = sortedUrls;

  const lineOrder = useMemo(() => data?.series_url_order || [], [data?.series_url_order]);
  const chartData = useMemo(
    () => (data?.series || []).map((row) => ({ date: row.date, label: formatShortDate(row.date), ...(row.metrics || {}) })),
    [data?.series]
  );
  const lineLabels = useMemo(() => {
    const obj = {};
    lineOrder.forEach((url) => { obj[url] = urlShortForLegend(url); });
    return obj;
  }, [lineOrder]);

  const totalCitations = useMemo(
    () => (data?.type_distribution || []).reduce((s, t) => s + t.count, 0),
    [data?.type_distribution]
  );

  const yMax = useMemo(() => {
    const vals = chartData.flatMap((d) => lineOrder.map((k) => (typeof d[k] === 'number' ? d[k] : 0)));
    const peak = vals.length ? Math.max(...vals) : 0;
    return Math.min(100, Math.ceil((peak + 5) / 5) * 5);
  }, [chartData, lineOrder]);

  const renderTooltip = useCallback(({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="aivis-dash-tooltip">
        <div className="aivis-dash-tooltip-title">{label}</div>
        {payload.filter((p) => p.dataKey !== 'label' && p.dataKey !== 'date').map((p) => (
          <div key={String(p.dataKey)} className="aivis-dash-tooltip-row">
            <span className="aivis-dash-tooltip-dot" style={{ background: p.color }} />
            <span>{lineLabels[p.dataKey] || p.name}</span>
            <strong>{typeof p.value === 'number' ? `${p.value}%` : p.value}</strong>
          </div>
        ))}
      </div>
    );
  }, [lineLabels]);

  if (!selectedCampaignId) {
    return <NoClientState />;
  }

  return (
    <div className="aivis-domains-page">
      <header className="aivis-dash-header">
        <div>
          <h1 className="aivis-page-title">Cited URLs</h1>
          <p className="aivis-page-sub">
            {selected
              ? `All URLs cited in AI responses for "${selected.client_name}".`
              : 'Select a campaign to view sources.'}
          </p>
        </div>
      </header>

      {error ? <div className="aivis-auth-error">{error}</div> : null}
      {snapshotBuilding ? (
        <p className="aivis-page-sub" aria-live="polite">
          Preparing cited-URL insights from completed runs. This page will update automatically.
        </p>
      ) : null}

      {/* Section 1: chart 70% + domain-type distribution 30% */}
      <section className="aivis-dash-section" aria-label="Overview">
        <div className="aivis-dash-row aivis-sources-row--70-30">
          <div className="aivis-dash-card">
            <div className="aivis-dash-card-head">
              <h2 className="aivis-dash-card-title">Source Citations by URL</h2>
              <p className="aivis-dash-card-desc">% of chats where each top URL appeared as a source (by day).</p>
            </div>
            <div className="aivis-dash-chart-wrap aivis-dash-chart-wrap--tall">
              {snapshotBuilding ? (
                <p className="aivis-dash-empty">Gathering cited-URL data from completed runs…</p>
              ) : loading && !chartData.length ? (
                <p className="aivis-dash-empty">Loading…</p>
              ) : chartData.length && lineOrder.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={tokens.graphite200} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={tokens.graphite500} />
                    <YAxis domain={[0, yMax]} tick={{ fontSize: 11 }} stroke={tokens.graphite500} width={36} tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={renderTooltip} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => lineLabels[v] || v} />
                    {lineOrder.map((key, i) => (
                      <Line key={key} type="monotone" dataKey={key} name={key}
                        stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="aivis-dash-empty">
                  {selectedCampaignId ? 'No source data yet. Run a campaign to see trends.' : 'Select a campaign.'}
                </p>
              )}
            </div>
          </div>

          <div className="aivis-dash-card">
            <UrlTypeDistributionPanel items={data?.type_distribution || []} totalSources={totalCitations} />
          </div>
        </div>
      </section>

      {/* Section 2: sources table */}
      <section className="aivis-dash-section" aria-label="Sources table">
        <div className="aivis-dash-card">
          <div className="aivis-domains-table-toolbar">
            <div className="aivis-prompts-search-wrap">
              <SearchIcon />
              <input type="search" className="aivis-prompts-search" placeholder="Search sources"
                value={search} onChange={handleSearchChange} aria-label="Search sources" />
            </div>
            {urlTypeOptions.length > 0 && (
              <div className="aivis-sources-type-filter">
                <span className="aivis-sources-type-filter-dot" style={{ background: typeFilter ? typeColor(typeFilter) : 'var(--aivis-text-subtle)' }} aria-hidden />
                <select
                  className="aivis-sources-type-select"
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                  aria-label="Filter by URL type"
                >
                  <option value="">All URL types</option>
                  {urlTypeOptions.map((t) => (
                    <option key={t.classifier} value={t.classifier}>{t.label}</option>
                  ))}
                </select>
                {typeFilter && (
                  <button type="button" className="aivis-sources-type-clear" onClick={() => { setTypeFilter(''); setPage(1); }} aria-label="Clear filter">×</button>
                )}
              </div>
            )}

            {/* Fit Score filter */}
            <div className="aivis-sources-type-filter">
              <select
                className="aivis-sources-type-select"
                value={fitFilter}
                onChange={(e) => { setFitFilter(e.target.value); setPage(1); }}
                aria-label="Filter by Fit Score"
              >
                <option value="">All Fit Scores</option>
                <option value="good">Good Fit</option>
                <option value="moderate">Moderate Fit</option>
                <option value="poor">Poor Fit</option>
              </select>
              {fitFilter && (
                <button type="button" className="aivis-sources-type-clear" onClick={() => { setFitFilter(''); setPage(1); }} aria-label="Clear fit filter">×</button>
              )}
            </div>

            {/* Sent to n8n filter — Visibility Labs only, not for viewers */}
            {showN8nColumn && (
              <div className="aivis-sources-type-filter">
                <select
                  className="aivis-sources-type-select"
                  value={n8nFilter}
                  onChange={(e) => { setN8nFilter(e.target.value); setPage(1); }}
                  aria-label="Filter by n8n status"
                >
                  <option value="">All n8n status</option>
                  <option value="sent">Sent to n8n</option>
                  <option value="not_sent">Not sent</option>
                </select>
                {n8nFilter && (
                  <button type="button" className="aivis-sources-type-clear" onClick={() => { setN8nFilter(''); setPage(1); }} aria-label="Clear n8n filter">×</button>
                )}
              </div>
            )}
            {sortedUrls.length > 0 && (
              <button type="button" className="aivis-csv-export-btn" onClick={handleExportCsv} title="Export visible rows as CSV">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
            )}
          </div>

          <div className="aivis-domains-table-scroll">
            <table className="aivis-domains-table">
              <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: showN8nColumn ? '28%' : '34%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '12%' }} />
                {showN8nColumn && <col style={{ width: '11%' }} />}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="aivis-domains-th-num">#</th>
                  <ColHeader label="URL" sortKey="url" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.url} />
                  <ColHeader label="URL type" sortKey="url_type_label" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.url_type} centered />
                  <ColHeader label="Client Mentioned" sortKey="client_mentioned" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.client_mentioned} centered />
                  <ColHeader label="Times Cited" sortKey="times_cited" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.times_cited} centered />
                  <ColHeader label="Fit Score" sortKey="relevance_check" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.relevance_check} centered />
                  {showN8nColumn && (
                    <ColHeader label="Sent to n8n" sortKey="n8n_sent" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.n8n_sent} centered />
                  )}
                </tr>
              </thead>
              <tbody>
                {pageUrls.length === 0 ? (
                  <tr>
                    <td colSpan={showN8nColumn ? 7 : 6} className="aivis-domains-empty">
                      {snapshotBuilding
                        ? 'Gathering cited-URL data from completed runs…'
                        : loading ? 'Loading…'
                          : (search || typeFilter || fitFilter || n8nFilter) ? 'No sources match your filters.'
                            : !selectedCampaignId ? 'Select a campaign.'
                              : totalCitations > 0
                                ? `These ${totalCitations.toLocaleString()} cited URLs haven't been scored for Fit yet. They'll appear here once scoring completes.`
                                : 'No source data yet.'}
                    </td>
                  </tr>
                ) : (
                  pageUrls.map((row, idx) => (
                    <tr
                      key={row.url}
                      className="aivis-domains-tr aivis-domains-tr--clickable"
                      onClick={() => navigate(`/ai-vis/sources/detail?url=${encodeURIComponent(row.url)}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(`/ai-vis/sources/detail?url=${encodeURIComponent(row.url)}`)}
                    >
                      <td className="aivis-domains-td-num aivis-domains-td-rank">
                        {(safePage - 1) * PAGE_SIZE + idx + 1}
                      </td>
                      <td className="aivis-url-td-url">
                        <div className="aivis-url-cell">
                          {row.page_title && (
                            <span className="aivis-url-title" title={row.page_title}>{row.page_title}</span>
                          )}
                          <a href={row.url.startsWith('http') ? row.url : `https://${row.url}`}
                            target="_blank" rel="noopener noreferrer"
                            className="aivis-url-href" title={row.url}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {urlDisplayFull(row.url)}
                          </a>
                        </div>
                      </td>
                      <td className="aivis-domains-td-type aivis-domains-td-center"><span className={typeClass(row.classifier)}>{row.url_type_label}</span></td>
                      <td className="aivis-domains-td-center">
                        <span className={`aivis-mentioned ${row.client_mentioned ? 'aivis-mentioned--yes' : 'aivis-mentioned--no'}`}>
                          {row.client_mentioned ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="aivis-domains-td-center">{row.times_cited}</td>
                      <td className="aivis-domains-td-center">
                        {row.relevance_check ? (
                          <span
                            className="aivis-sources-fit-badge"
                            style={{
                              background: (FIT_COLORS[row.relevance_check] || {}).bg || 'var(--aivis-bg)',
                              color: (FIT_COLORS[row.relevance_check] || {}).color || 'var(--aivis-text-muted)',
                            }}
                          >
                            {row.relevance_check}
                          </span>
                        ) : <span className="aivis-sources-fit-badge aivis-sources-fit-badge--none">—</span>}
                      </td>
                      {showN8nColumn && (
                        <td className="aivis-domains-td-center">
                          {row.n8n_sent ? (
                            <span className="aivis-sources-sent-badge aivis-sources-sent-badge--yes">Sent</span>
                          ) : (
                            <span className="aivis-sources-sent-badge aivis-sources-sent-badge--no">Not sent</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalUrls > PAGE_SIZE ? (
            <PaginationControls page={safePage} totalPages={totalPages} totalItems={totalUrls} onPage={(p) => setPage(p)} />
          ) : totalUrls > 0 ? (
            <p className="aivis-domains-count">{totalUrls.toLocaleString()} source{totalUrls === 1 ? '' : 's'}</p>
          ) : null}
          {!search && !typeFilter && !fitFilter && !n8nFilter && totalCitations > totalUrls && (
            <p className="aivis-domains-count">
              Showing {totalUrls.toLocaleString()} scored URL{totalUrls === 1 ? '' : 's'} of {totalCitations.toLocaleString()} cited. URLs appear here once they receive a Fit Score.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
